import { type GetApiTracksQueryParams } from '@/shared/api'
import { combine, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import {
  jingleCreate,
  jingleDelete,
  jingleUpdate,
  trackMultipleDelete,
} from '../api'
import { TrackTypeEnum, TrackUploadStatus } from '../lib/track-type'
import {
  type TracksQueryModelWithPagination,
  type TracksSortState,
  createTracksQueryModel,
} from '../lib/tracks-query-model'
import { selectedJinglesDeleted } from './jingle.model'

interface JinglesTableGateProps {
  initialFilters?: GetApiTracksQueryParams
  initialPage?: number
  initialPageSize?: number
  initialSearch?: string
  initialSort?: TracksSortState
}
export const JinglesTableGate = createGate<JinglesTableGateProps>()

export const JinglesTableJinglesQueryModel = createTracksQueryModel({
  initialFilters: {
    Types: [TrackTypeEnum.Jingle],
    UploadStatuses: [TrackUploadStatus.Uploaded],
  },
  usePagination: true,
  accumulate: false,
}) as TracksQueryModelWithPagination
const { availableSet, unavailableSet } = JinglesTableJinglesQueryModel
export const {
  //   Query
  query: jinglesTableQuery,
  //   Init
  $filters: $jinglesTableFilters,
  filtersInit: jinglesTableFiltersInit,
  filtersChanged: jinglesTableFiltersChanged,
  filtersReset: jinglesTableFiltersReset,
  //   Search
  $search: $jinglesTableSearch,
  searchChanged: jinglesTableSearchChanged,
  searchReset: jinglesTableSearchReset,
  //   Sort
  $sort: $jinglesTableSort,
  sortChanged: jinglesTableSortChanged,
  sortReset: jinglesTableSortReset,
  //   Pagination
  $page: $jinglesTablePage,
  pageChanged: jinglesTablePageChanged,
  pageReset: jinglesTablePageReset,
  $pageSize: $jinglesTablePageSize,
  pageSizeChanged: jinglesTablePageSizeChanged,
  //   Refresh
  refreshed: jinglesTableRefreshed,
} = JinglesTableJinglesQueryModel

// Table Row Selection

export const jinglesTableSelectedRowKeysChanged = createEvent<React.Key[]>()
export const $jinglesTableSelectedRowKeys = createStore<React.Key[]>([])
  .on(jinglesTableSelectedRowKeysChanged, (_, payload) => payload)
  .reset(jinglesTableQuery.finished.finally)
export const $areJinglesTableRowsSelected = $jinglesTableSelectedRowKeys.map(
  (keys) => keys.length > 0,
)
export const selectedJinglesDeleteRequested = createEvent()

sample({
  source: $jinglesTableSelectedRowKeys,
  clock: selectedJinglesDeleteRequested,
  fn: (keys) => ({ trackIds: keys.map(String) }),
  target: selectedJinglesDeleted,
})

sample({
  clock: JinglesTableGate.open,
  target: availableSet,
})

// Search input

export const $jinglesTableSearchInputValue = createStore('')
  .on(jinglesTableSearchChanged, (_, q) => q)
  .reset(jinglesTableSearchReset)

// Setup initial params
// Page
sample({
  clock: JinglesTableGate.open,
  source: combine(
    JinglesTableGate.state,
    ({ initialPage }) => initialPage || null,
  ),
  filter: (page) => page !== null,
  target: jinglesTablePageChanged,
})
// Page Size
sample({
  clock: JinglesTableGate.open,
  source: combine(
    JinglesTableGate.state,
    ({ initialPageSize }) => initialPageSize || null,
  ),
  filter: (pageSize) => pageSize !== null,
  target: jinglesTablePageSizeChanged,
})
// Search
sample({
  clock: JinglesTableGate.open,
  source: combine(
    JinglesTableGate.state,
    ({ initialSearch }) => initialSearch || null,
  ),
  filter: (search) => search !== null,
  target: jinglesTableSearchChanged,
})
// Sort
sample({
  clock: JinglesTableGate.open,
  source: combine(
    JinglesTableGate.state,
    ({ initialSort }) => initialSort || null,
  ),
  filter: (sort) => sort !== null,
  target: jinglesTableSortChanged,
})
// Filters
sample({
  source: JinglesTableGate.state,
  clock: availableSet,
  fn: ({ initialFilters: params }) => params || {},
  target: jinglesTableFiltersInit,
})

sample({
  clock: JinglesTableGate.close,
  target: unavailableSet,
})

// Decrease page if there is only one record on page and it's being deleted
sample({
  source: combine({
    page: $jinglesTablePage,
    queryData: jinglesTableQuery.$data,
  }),
  clock: [jingleDelete.finished.success, trackMultipleDelete.finished.success],
  filter: ({ queryData, page }) =>
    !queryData?.items.length || (queryData?.items.length <= 1 && page > 1),
  fn: ({ page }) => page - 1,
  target: jinglesTablePageChanged,
})

sample({
  clock: [
    jingleCreate.finished.success,
    jingleUpdate.finished.success,
    jingleDelete.finished.success,
    trackMultipleDelete.finished.success,
  ],
  target: jinglesTableRefreshed,
})
